import { getSdk } from '@monterosa-sdk/core';
import { Emitter, setTimestamp, memoizePromise } from '@monterosa-sdk/util';
import Enmasse from '@monterosa-sdk/enmasse-kit';

/**
 * @license
 * types.ts
 * connect-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-01-19
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * @internal
 */
const KIT_NAME = 'connect';
/**
 * @internal
 */
var InitState;
(function (InitState) {
    InitState["NotInitialised"] = "not_initialised";
    InitState["Initialised"] = "initialised";
    InitState["Initialising"] = "initialising";
})(InitState || (InitState = {}));
/**
 * A list of possible connection states. Can be used with
 * {@link Connect.state} to determine the current state.
 *
 * By default {@link Connect} is in the disconnected state.
 * When {@link connect} invoked `Connect` state switches to
 * `ConnState.Connecting` and connection is maintained permanently until
 * {@link disconnect} method is invoked. If connection was unsucessful,
 * a new attempt will start after a short delay and `ConnState.connecting`
 * is triggered. Delays between attempts increased based on this sequence:
 * 0, 3, 5, 10, 20, 30, 45, 60 where 60 seconds is the max.
 *
 * If `Connect` was previously connected and for any reason connection is lost,
 * the state changed to `ConnState.Connecting` again and the connection process
 * starts again automatically.
 *
 * @example
 * ```javascript
 * if (conn.state === ConnState.Connecting) {
 *   // display "connecting" message
 * }
 * ```
 */
var ConnState;
(function (ConnState) {
    /**
     * Disconnected state
     */
    ConnState["Disconnected"] = "disconnected";
    /**
     * Connecting state
     */
    ConnState["Connecting"] = "connecting";
    /**
     * Connected state
     */
    ConnState["Connected"] = "connected";
})(ConnState || (ConnState = {}));
/**
 * Class of the {@link Message}
 */
var Klass;
(function (Klass) {
    /**
     * Authentication request. Sent by server on client connection.
     */
    Klass["Authenticate"] = "auth";
    /**
     * Authentication response. Sent by client as response
     * to authentication request.
     */
    Klass["AuthenticateResponse"] = "authr";
    /**
     * Authentication acknowledgement. Sent by the server as confirmation
     * of successful authentication.
     */
    Klass["AuthenticateSuccess"] = "authok";
    /**
     * Ping message is sent by server periodically as a keep-alive.
     * Ping message is only sent if no messages are sent for specific time
     * period, meaning if there are other messages sent frequently enough
     * then ping messages are not sent at all.
     */
    Klass["Ping"] = "ping";
    /**
     * Subscribe to a channel. Sent by the client to subscribe to a channel
     */
    Klass["Subscribe"] = "sub";
    /**
     * Unsubscribe from a channel. Sent by the client to unsubscribe from a channel
     */
    Klass["Unsububscribe"] = "unsub";
    /**
     * End of cache. Sent by the server to notify that all messages are sent from
     * a channel cache
     */
    Klass["EndOfCache"] = "eoc";
    /**
     * User login. Sent by client to supply verified user identity to the server.
     */
    Klass["Login"] = "login";
    /**
     * User login success
     */
    Klass["LoginSuccess"] = "login_ok";
    /**
     * User login failure
     */
    Klass["LoginFail"] = "login_fail";
    /**
     * Averages counters. Client can submit a counter which is processed together
     * with other counters to determine the average of all counters within specific
     * demographics segment.
     */
    Klass["Counter"] = "counter";
    /**
     * Client votes. Sents by a user to vote for a poll option
     */
    Klass["Vote"] = "v";
})(Klass || (Klass = {}));

/**
 * @license
 * helpers.ts
 * connect-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-09-14
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
function onState(conn, needle, callback) {
    const handler = (state, ...rest) => {
        if (state === needle) {
            callback(...rest);
        }
    };
    conn.on('state', handler);
    return () => conn.off('state', handler);
}

/* eslint class-methods-use-this: "off" */
/**
 * @internal
 */
class ConnectImpl extends Emitter {
    constructor() {
        super();
        this.initState = InitState.NotInitialised;
        this.initPromise = Promise.resolve();
        Enmasse.bind(Enmasse.ON_STATE, (state, ...rest) => {
            if (state === Enmasse.STATE_CONNECTED) {
                const [timestamp] = rest;
                setTimestamp(timestamp);
            }
            this.emit('state', ConnectImpl.toConnState(state), ...rest);
        });
        Enmasse.bind(Enmasse.ON_MESSAGE, (message) => {
            this.emit('message', message);
        });
    }
    get state() {
        return ConnectImpl.toConnState(Enmasse.getState());
    }
    static getInstance() {
        if (!ConnectImpl.instance) {
            ConnectImpl.instance = new ConnectImpl();
        }
        return ConnectImpl.instance;
    }
    static toConnState(state) {
        switch (state) {
            default:
            case Enmasse.STATE_DISCONNECTED:
                return ConnState.Disconnected;
            case Enmasse.STATE_CONNECTING:
                return ConnState.Connecting;
            case Enmasse.STATE_CONNECTED:
                return ConnState.Connected;
        }
    }
    async init(host) {
        if (this.initState === InitState.Initialising ||
            this.initState === InitState.Initialised) {
            return this.initPromise;
        }
        this.initPromise = new Promise((resolve, reject) => {
            this.initState = InitState.Initialising;
            Enmasse.bind(Enmasse.ON_READY, () => {
                this.initState = InitState.Initialised;
                Enmasse.unbind(Enmasse.ON_READY);
                Enmasse.unbind(Enmasse.ON_ERROR);
                resolve();
            });
            Enmasse.bind(Enmasse.ON_ERROR, (err) => {
                this.initState = InitState.NotInitialised;
                Enmasse.unbind(Enmasse.ON_READY);
                Enmasse.unbind(Enmasse.ON_ERROR);
                reject(new Error(`Unable to initialise connect kit: ${err}`));
            });
            Enmasse.init({
                config: `https://${host}/config/enmasse.json`,
            });
        });
        return this.initPromise;
    }
    async connect() {
        if (this.initState !== InitState.Initialised) {
            throw new Error('must be initialised before connecting');
        }
        if (this.state === ConnState.Connected) {
            throw new Error('already connected');
        }
        if (this.state === ConnState.Connecting) {
            throw new Error('already connecting');
        }
        return new Promise((resolve, reject) => {
            const handler = (state) => {
                if (state === ConnState.Connected) {
                    Enmasse.unbind(Enmasse.ON_STATE, handler);
                    resolve();
                }
                if (state === ConnState.Disconnected) {
                    Enmasse.unbind(Enmasse.ON_STATE, handler);
                    reject(new Error('disconnected'));
                }
            };
            Enmasse.bind(Enmasse.ON_STATE, handler);
            Enmasse.connect();
        });
    }
    disconnect() {
        if (this.state !== ConnState.Connected) {
            throw new Error('must be connected before disconnecting');
        }
        Enmasse.disconnect();
    }
    subscribe(channel) {
        return new Promise((resolve) => {
            const handler = (ch) => {
                if (ch !== channel)
                    return;
                Enmasse.unbind(Enmasse.ON_EOC, handler);
                resolve();
            };
            Enmasse.bind(Enmasse.ON_EOC, handler);
            Enmasse.subscribe(channel);
        });
    }
    unsubscribe(channel) {
        Enmasse.unsubscribe(channel);
    }
    send(channel, klass, body) {
        Enmasse.send(channel, klass, body);
    }
    async login(userId, timestamp, signature) {
        return new Promise((resolve, reject) => {
            Enmasse.login(userId, timestamp, signature, resolve, () => {
                reject(new Error('Login failure'));
            });
        });
    }
    delete() { }
}

/**
 * @license
 * connect.ts
 * connect-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-01-19
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * @internal
 */
const getConnectMemoized = memoizePromise(async (host) => {
    const conn = ConnectImpl.getInstance();
    await conn.init(host);
    return conn;
}, (host) => host);
/**
 * Returns the {@link Connect} instance associated with the provided
 * host. If host is not provided, it returns Connect instance for
 * the host associated with the default sdk.
 *
 * @param sdk - Monterosa SDK.
 */
function getConnect(host) {
    if (host === undefined) {
        const sdk = getSdk();
        host = sdk.options.host;
    }
    return getConnectMemoized(host);
}
/**
 * Adds an observer for when connection state changed to connected.
 *
 * @param conn - The {@link Connect} instance
 * @param callback - The callback triggered when {@link ConnState | state}
 *   changed to connected
 */
function onConnected(conn, callback) {
    return onState(conn, ConnState.Connected, callback);
}
/**
 * Adds an observer for when connection state changed to connecting.
 *
 * @param conn - The {@link Connect} instance
 * @param callback - callback triggered on connecting state. Two parameters
 *   are provided: `attempt` — sequential number of connect attempt and
 *   `delay` - delay in seconds before the next attempt
 */
function onConnecting(conn, callback) {
    return onState(conn, ConnState.Connecting, callback);
}
/**
 * Adds an observer for when connection state changed to disconnected.
 *
 * @param conn - The {@link Connect} instance
 * @param callback - callback triggered on disconnected state
 */
function onDisconnected(conn, callback) {
    return onState(conn, ConnState.Disconnected, callback);
}
/**
 * Adds an observer for when a new message received
 *
 * @example
 * ```javascript
 * const unsubscribe = onMessage(conn, (message) => {
 *   console.log(message.body);
 * });
 * ```
 *
 * @param conn - The {@link Connect} instance
 * @param callback - The callback function triggered on message receive
 */
function onMessage(conn, callback) {
    const handler = (message) => {
        callback(message);
    };
    conn.on('message', handler);
    return () => conn.off('message', handler);
}
/**
 * @internal
 */
const connectMemoized = memoizePromise(async (conn) => {
    await conn.connect();
}, (conn) => conn, {
    clearOnResolve: true,
    clearOnReject: true,
});
/**
 * Starts a connection process. If connect is successful a promise resolved by
 * and {@link onConnected} is triggered, otherwise a next attempt will be
 * automatically invoked and {@link onConnecting} is triggered.
 *
 * SDK connects automatically when its created. Connection procedure can be
 * aborted by calling {@link disconnect} function and the promise is rejected.
 *
 * @example
 * ```javascript
 * try {
 *   const conn = await getConnect();
 *
 *   await connect(conn);
 *
 *   console.log('connected!');
 * } catch (err) {
 *   console.log(err)
 * }
 * ```
 *
 * State of the connection can be handled separately
 *
 * @example
 * ```javascript
 * onConnected(() => console.log('connected'));
 *
 * onConnecting((attempt, delay) => console.log('connecting'));
 *
 * onDisconnected(() => console.log('disconnected'));
 * ```
 *
 * @param conn - The {@link Connect} instance
 * @param host - Interaction Cloud Studio host.
 *  Can be found in Project Settings in API tab
 */
function connect(conn) {
    return connectMemoized(conn);
}
/**
 * Disconnect from the server and triggers {@link onDisconnected}
 *
 * @param conn - The {@link Connect} instance
 */
function disconnect(conn) {
    conn.disconnect();
}
/**
 * @internal
 */
const subscribeMemoized = memoizePromise(async (conn, channel) => conn.subscribe(channel), (conn, channel) => channel);
/**
 * Subscribe to the server channel.
 *
 * Any message published to a channel is {@link onMessage | received}
 * only by subscribers to that channel. The sdk out of the box handles
 * all required subscriptions internally and {@link onMessage | handles messages}
 * so there is no need in to subscribe by developers.
 *
 * To stop receiving messages on a channel {@link unsubscribe} should be called.
 *
 * @param conn - The {@link Connect} instance
 * @param channel - Channel name
 */
function subscribe(conn, channel) {
    return subscribeMemoized(conn, channel);
}
/**
 * Unsubscribe from the server channel.
 *
 * Removes subscription to the channel so that recipient no longer receive
 * messages from that channel.
 *
 * @param conn - The {@link Connect} instance
 * @param channel - Channel name
 */
function unsubscribe(conn, channel) {
    conn.unsubscribe(channel);
}
/**
 * Send message to the server.
 *
 * The sdk out of the box provides more high level functions that uses
 * this function to send data to the server. Though a developer still can
 * use it if he knows a message protocol (klass and body) and a channel
 * that expect the message.
 *
 * @param conn - The Connect instance
 * @param channel  - Channel name. Only {@link subscribe | subscribers}
 *   to that channel can send messages.
 * @param klass - Message class name.
 * @param body - Message payload
 */
function send(conn, channel, klass, body) {
    conn.send(channel, klass, body);
}
async function login(conn, userId, timestamp, signature) {
    const result = await conn.login(userId, timestamp, signature);
    return result;
}

export { ConnState, ConnectImpl, InitState, KIT_NAME, Klass, connect, connectMemoized, disconnect, getConnect, getConnectMemoized, login, onConnected, onConnecting, onDisconnected, onMessage, send, subscribe, subscribeMemoized, unsubscribe };
//# sourceMappingURL=index.esm2017.js.map
